import type {SiteResponse, SiteResponseRegion} from '@api/model';

/**
 * Shared site display vocabulary. Lives in `@/lib` (not the sites module) so
 * the sites listing, the site detail page and the app-chrome breadcrumb can
 * share one source of truth without a cross-module import — the same shape as
 * `@/lib/job-display`. Every caller imports them from here, the sites module
 * included. The region vocabulary and `formatBudget` are here for the same
 * reason: the sites, projects and visitor-group screens all read them.
 *
 * A site's active/inactive lifecycle is NOT here: it is the same two states
 * every other registry has, and lives in `@/lib/activity` (`Activity`,
 * `activityOf`, `selectableActive`, `ActivityPill`). The site-specific
 * `SiteActivity` / `SITE_ACTIVITY_*` / `siteActivity` / `selectableSites`
 * spellings this file used to own were one of three copies of that vocabulary.
 */

export type SiteRegion = SiteResponseRegion;

export const SITE_REGION_ORDER = [
  'central',
  'northern',
  'southern',
  'eastern',
  'sarawak',
  'sabah',
] as const satisfies readonly NonNullable<SiteRegion>[];

export const SITE_REGION_LABEL: Record<NonNullable<SiteRegion>, string> = {
  central: 'Central',
  northern: 'Northern',
  southern: 'Southern',
  eastern: 'Eastern',
  sarawak: 'Sarawak',
  sabah: 'Sabah',
};

export function siteRegionLabel(region: SiteRegion): string | null {
  return region ? SITE_REGION_LABEL[region] : null;
}

export function isSiteRegion(value: string): value is NonNullable<SiteRegion> {
  return (SITE_REGION_ORDER as readonly string[]).includes(value);
}

/** Format a site budget (`budget` is a decimal string from the API). */
export function formatBudget(budget: string | null): string {
  if (budget === null || budget === '') return '—';
  const n = Number(budget);
  if (!Number.isFinite(n)) return '—';
  return `RM ${n.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
}

/**
 * The site's full SQL Account code — `{CAT-PROJECT}-{CLIENT}-{SITE}`, e.g.
 * `OFF-FSOP-M001-ABNG` (DERP-242).
 *
 * Read straight off `sql_project_code`, which the Excel import writes as the
 * whole code while `site_code` keeps only the trailing site segment. It is NOT
 * recomposed from the project code plus `site_code`: the day-1 master-data
 * cleanup exists precisely because those two do not always tally, and a
 * recomposed string would paper over the mismatch this is meant to expose.
 *
 * Null when the site has no code yet — each caller picks its own fallback
 * (an em dash in the table, the site name in a breadcrumb), so an uncoded site
 * reads as a gap rather than as a blank.
 *
 * The key is required: every shape that reaches here carries it, and keeping it
 * so stops a caller projecting the field away and silently getting `null`.
 */
export function siteFullCode(site: Pick<SiteResponse, 'sql_project_code'>): string | null {
  return site.sql_project_code?.trim() || null;
}

/**
 * A site picker row: `CODE — Name`. The code leads because it is what Finance
 * keys payments off and how a site gets quoted ("that's the ABNG job") — the
 * order the Google Form's "Project Code" dropdown used.
 *
 * Falls back to the bare name, then to a short id tail: unpretty, but never a
 * blank row, because every site a picker offers has to stay selectable.
 * Contrast `useAllSites.codeOptions`, which drops code-less sites — it backs a
 * filter over a SQL Account export they cannot match at all.
 */
export function siteLabel(site: Pick<SiteResponse, 'id' | 'name' | 'sql_project_code'>): string {
  const name = site.name.trim() || `Site ${site.id.slice(0, 8)}`;
  const code = siteFullCode(site);
  return code ? `${code} — ${name}` : name;
}

/** Short, scannable site reference: site_code if set, else a UUID projection. */
export function siteRef(site: Pick<SiteResponse, 'id' | 'site_code'>): string {
  if (site.site_code) return site.site_code;
  return `ST-${site.id.replace(/-/g, '').slice(0, 6).toUpperCase()}`;
}
